/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ClientPortal } from './ClientPortal';
import { cn } from '../lib/cn';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import { m, AnimatePresence } from 'motion/react';
import { LogoMark } from './LogoMark';
import { getComparerHref } from '../lib/compareAnchor';
import { COMPARER_START_ID, isProgrammaticScroll, onNewPageTop } from '../lib/pageScroll';
import { createScrollChromeTracker } from '../lib/scrollChrome';
import { HEADER_STATE_ATTR, setHeaderState } from '../lib/headerFollow';
import { lockPageScroll } from '../lib/scrollLock';
import { SURFACES, trackEvent } from '../lib/gtag';
import { providerHubPath } from '../lib/providerRoutes.js';

interface NavLink {
  name: string;
  path: string;
  primary?: boolean;
}

/**
 * A top-level entry that is BOTH a destination and a disclosure: clicking the
 * name goes to `path` (the section's own page), the chevron beside it opens
 * `children`. That list repeats `path` as its first option, so the panel is a
 * complete section index on its own and a keyboard user who opened it never
 * has to back out to reach the landing page. In the drawer the group flattens
 * to parent + indented children and the repeat is dropped.
 */
interface NavGroup {
  id: string;
  name: string;
  path: string;
  children: NavLink[];
}

type NavEntry = NavLink | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

/**
 * The row has ONE menu: at most one group is open at a time, and the state
 * lives in Header, not in each group. `pinned` = opened by a click, a tap,
 * Enter/Space or ArrowDown — such a menu survives the pointer leaving it.
 * `swapped` = reached straight from another panel that was still on screen
 * (open, or fading out): the incoming panel then appears without its entrance
 * transition and the outgoing one disappears without its fade, so two panels
 * are never visible together. A first open and a final close keep the fade.
 */
interface MenuState {
  id: string;
  pinned: boolean;
  swapped: boolean;
}

/**
 * Hover intent: the pointer has to stay on a group this long before its panel
 * opens, so a pointer crossing the row on its way to the page does not flash
 * panels open. Until 2026-10-03 every pointerenter opened at once, and a sweep
 * across the row left three panels on screen together. 120ms, not less: the
 * chips are 107–181px wide at 1280, so an ordinary 500–640ms sweep rests 70–
 * 150ms on each, and at 80ms that still opened one to four panels in turn. A
 * pointer that stops on a name waits 120ms — still immediate to the eye.
 */
const HOVER_OPEN_MS = 120;
/**
 * Grace after the pointer leaves an unpinned menu: a diagonal move from the
 * name towards the panel, or a brief overshoot, does not slam it shut.
 */
const HOVER_CLOSE_MS = 150;
/** The panels' own fade (duration-150 on open, the 150ms close transition). */
const PANEL_FADE_MS = 150;

/**
 * How long after a focus move a scroll still counts as the browser bringing
 * the focused control into view, not as the visitor reading. Chromium and
 * WebKit make that scroll instantly, so its one scroll event arrives within a
 * frame or two; the margin covers a busy main thread and a smooth focus
 * scroll. A wheel or swipe that starts this soon after a Tab only loses its
 * first fraction of a second to the header.
 */
const FOCUS_SCROLL_WINDOW_MS = 400;

// One look for every desktop nav item — the plain links and the group's chip
// share these so the group can't drift from its neighbours. `leading-5` keeps
// the 13px lg type on a 20px line, so every chip is 36px (28px compact) at
// every width instead of 35.5px at lg.
const NAV_ITEM_BASE =
  'px-2 xl:px-4 py-2 lg:group-data-[compact]:py-1 text-[13px] xl:text-sm leading-5 font-bold rounded-xl transition-all whitespace-nowrap';
const NAV_ITEM_IDLE = 'text-slate-400 hover:bg-slate-800 hover:text-emerald-500';
// An open group wears the hover look. NAV_ITEM_ACTIVE means "you are here":
// worn by an open panel it made two chips read as current on a section page.
const NAV_ITEM_OPEN = 'bg-slate-800 text-emerald-500';
const NAV_ITEM_ACTIVE = 'bg-emerald-900/20 text-emerald-300';
// A group chip is a link and a chevron button, measured so the pair reads as
// one item with the same insets as a plain link (8px at lg, 16px at xl):
//  - the label starts at the item inset (pl-2 xl:pl-4);
//  - label → chevron ink ≈ 5.5px: 1.5px of link padding, 1.5px of padding
//    around the icon and the svg's own side bearing (the path starts at x=6
//    of 24, minus half the 2.5 stroke: 2.4px at 12px, 2.8px at 14px);
//  - chevron ink → chip edge ≈ the item inset: bearing + 1.5 + pr-1 / pr-3;
//  - the button stretches to the chip's height, so no strip above or below it
//    belongs to neither control. At lg it is 19px wide; its ::after reaches
//    6px into the empty left padding of the next item (z-10 wins the hit test
//    over a neighbouring group's positioned root), for a 25px target that
//    never covers the label's own glyphs.
// Focus: 5.5px between label and chevron leaves no room for two outset rings
// (each reaches 4px out; the chevron's cut into the label's last letter). The
// name link therefore rings the whole chip — the same ring, at the same
// distance, as a plain item — and the chevron rings only the box around its
// icon, inset: 20px tall like the label's line, symmetric around the ink, its
// left side centred in the seam (the 1.5 + 1.5px split).
const NAV_GROUP_LINK =
  'pl-2 xl:pl-4 pr-[1.5px] py-2 lg:group-data-[compact]:py-1 text-[13px] xl:text-sm leading-5 font-bold whitespace-nowrap transition-all rounded-l-xl outline-none';
const NAV_GROUP_CHEVRON =
  "group/chevron relative inline-flex items-center self-stretch pr-1 xl:pr-3 outline-none after:absolute after:inset-y-0 after:left-0 after:-right-1.5 after:z-10 after:content-['']";
const NAV_GROUP_CHEVRON_MARK =
  'inline-flex h-5 items-center rounded-md px-[1.5px] group-focus-visible/chevron:ring-2 group-focus-visible/chevron:ring-inset group-focus-visible/chevron:ring-focus-ring';
// The chip rings itself when its name link has keyboard focus (focus-ring's
// ring, offset and colours, keyed on the child instead of the element).
const NAV_GROUP_CHIP_FOCUS =
  'has-[>a:focus-visible]:ring-2 has-[>a:focus-visible]:ring-offset-2 has-[>a:focus-visible]:ring-focus-ring has-[>a:focus-visible]:ring-offset-surface-base';

interface NavGroupDisclosureProps {
  group: NavGroup;
  /** Accessible name for the chevron, e.g. "Toon opties onder Over ons". */
  toggleLabel: string;
  /** This section's page is the current one → the whole chip reads as active. */
  active: boolean;
  isActive: (path: string) => boolean;
  /** This group's panel is the row's open menu. */
  open: boolean;
  /** It was reached straight from another visible panel: no entrance transition. */
  swapped: boolean;
  /** Another group's panel is open: this one, if it was showing, leaves without a fade. */
  othersOpen: boolean;
  /** Mouse pointer entered / left the group (chip, hover bridge or panel). */
  onHoverStart: () => void;
  onHoverEnd: () => void;
  /** The chevron was activated: click, tap, Enter or Space. */
  onToggle: () => void;
  /** ArrowDown on the chevron: open (pinned) — focus then moves to the first option. */
  onOpen: () => void;
  /** Escape, focus leaving the group, a pointerdown outside every group. */
  onClose: () => void;
  onNavigate: (link: NavLink) => void;
}

/**
 * WAI-ARIA disclosure navigation WITH A TOP-LEVEL LINK: the name is a real
 * link to the section page, and a separate chevron button (aria-expanded +
 * aria-controls) opens a list of ordinary links — no role="menu", that is for
 * application menus and changes how screen readers announce the links.
 * Splitting the two is what lets one item both navigate and disclose: a single
 * control cannot do both without guessing which the visitor meant. Mouse users
 * also get the panel on hover (after HOVER_OPEN_MS); touch and keyboard use the
 * chevron. A click on the chevron of a menu the hover already opened pins it
 * rather than closing it — the visitor asked for the menu and is looking at it
 * — and the next click closes it. Controlled: Header owns the one open menu.
 * The panel is ALWAYS mounted and only `inert` + invisible while closed, so
 * the links are in the prerendered HTML for crawlers (the desktop row is the
 * site's server-rendered nav) while assistive tech and the tab order skip them
 * until opened.
 */
const NavGroupDisclosure: React.FC<NavGroupDisclosureProps> = ({
  group,
  toggleLabel,
  active,
  isActive,
  open,
  swapped,
  othersOpen,
  onHoverStart,
  onHoverEnd,
  onToggle,
  onOpen,
  onClose,
  onNavigate,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusFirstOnOpen = useRef(false);
  const panelId = useId();

  // A pointerdown outside every group closes. One inside another group does
  // not: its own click decides (a chevron swaps to that group, a name
  // navigates), and closing here first would turn the swap into a fade-out
  // followed by a fade-in. pointerdown covers mouse, pen and touch alike.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element && e.target.closest('[data-nav-group]'))) onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onClose]);

  // ArrowDown on the button opens AND moves focus to the first link; done
  // after the commit so the panel is no longer inert when focus lands.
  useEffect(() => {
    if (open && focusFirstOnOpen.current) {
      focusFirstOnOpen.current = false;
      panelRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    }
  }, [open]);

  return (
    <div
      data-nav-group={group.id}
      className="relative"
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') onHoverStart();
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') onHoverEnd();
      }}
      onBlur={(e) => {
        // Tabbing out of the group closes it; moving between the button and
        // its links does not.
        if (open && !e.currentTarget.contains(e.relatedTarget as Node | null)) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          onClose();
          buttonRef.current?.focus();
        }
      }}
    >
      {/* One chip, two controls: the wrapper carries the tint and the hover so
          the pair still reads as a single nav item, while the link and the
          chevron stay transparent and keep their own focus rings. */}
      <div
        className={cn(
          'flex items-center rounded-xl',
          NAV_GROUP_CHIP_FOCUS,
          // A swap is instant for the chip as well: the group that loses the
          // menu drops its open look in the frame its panel disappears.
          othersOpen ? 'transition-none' : 'transition-all',
          active ? NAV_ITEM_ACTIVE : open ? NAV_ITEM_OPEN : NAV_ITEM_IDLE
        )}
      >
        <Link
          to={group.path}
          onClick={() => onNavigate({ name: group.name, path: group.path })}
          className={NAV_GROUP_LINK}
        >
          {group.name}
        </Link>
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          onKeyDown={(e) => {
            // ArrowDown lives on the chevron only. On the link it would eat a
            // plain "scroll the page down" keypress.
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (open) {
                // Already open (hover got there first): no state change, so no
                // effect — move focus directly.
                panelRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
              } else {
                focusFirstOnOpen.current = true;
                onOpen();
              }
            }
          }}
          className={NAV_GROUP_CHEVRON}
        >
          <span className={NAV_GROUP_CHEVRON_MARK}>
            <ChevronDown
              aria-hidden="true"
              strokeWidth={2.5}
              className={cn(
                'w-3 h-3 xl:w-3.5 xl:h-3.5 transition-transform duration-200 motion-reduce:transition-none',
                open && 'rotate-180'
              )}
            />
          </span>
          <span className="sr-only">{toggleLabel}</span>
        </button>
      </div>
      {/* Placement, measured against the header (80px row + 1px border):
          - vertical: the panel's top edge sits 8px below the header's bottom
            border. The chip is 36px tall and centred in the 80px row, so its
            bottom is 23px above that border: 23 + 8 = 31px. Compact, the
            28px chip ends 7px above the 41px bar's border: 7 + 8 = 15px.
          - horizontal: the first option's text starts under the label's text.
            The option text sits 23px inside the panel (1px border, p-1.5,
            px-4), the label 8px (lg) / 16px (xl) inside the chip.
          The offset is padding on the positioned wrapper, not margin: the
          strip between the chip and the panel then belongs to the group, so
          the pointer never "leaves" on the way down. */}
      <div
        id={panelId}
        ref={panelRef}
        inert={!open}
        className={cn(
          'absolute top-full -left-[15px] xl:-left-[7px] pt-[31px] lg:group-data-[compact]:pt-[15px] motion-reduce:transition-none',
          // Visibility keeps the closed panel out of find-in-page and out of
          // focus() on browsers without inert. It has to flip instantly on
          // open — ArrowDown focuses a link in the same tick, and a link that
          // is still computed hidden refuses focus — and only after the fade
          // on close. Hence two transition lists instead of one. A swap skips
          // both: the panel that leaves and the one that arrives change in
          // the same frame, and the open one sits above (z-20 over z-10).
          open
            ? cn(
                'z-20 visible opacity-100 translate-y-0',
                swapped ? 'transition-none' : 'transition-[opacity,transform] duration-150'
              )
            : cn(
                'z-10 invisible opacity-0 -translate-y-1 pointer-events-none',
                othersOpen
                  ? 'transition-none'
                  : '[transition:opacity_150ms,transform_150ms,visibility_0s_150ms]'
              )
        )}
      >
        <ul className="min-w-[13rem] rounded-2xl border border-slate-800/70 bg-slate-900 p-1.5 shadow-2xl shadow-black/30">
          {group.children.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                onClick={() => onNavigate(link)}
                className={cn(
                  // The row's type: 13px at lg, 14px from xl, on a 20px line.
                  'block rounded-xl px-4 py-2.5 text-[13px] xl:text-sm leading-5 font-bold whitespace-nowrap transition-colors focus-ring',
                  isActive(link.path)
                    ? NAV_ITEM_ACTIVE
                    : 'text-slate-300 hover:bg-slate-800 hover:text-emerald-500'
                )}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const Header: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // "Vergelijken" is the primary (filled) nav item and goes to where the
  // comparison starts (getComparerHref): the top of the homepage from `sm` —
  // Stap 1 (Bereken) sits in the hero — and the results on a phone.
  //
  // Every item shows from lg, where the desktop row starts. "Hoe wij
  // vergelijken" briefly rode a `hidden xl:block`, which opened a dead band at
  // 1024–1279px: the hamburger drawer that carries the full list only exists
  // BELOW lg, so between lg and xl the link was reachable from nowhere in the
  // header. Nothing may be hidden that way again, so the row keeps its six
  // items and the extra destinations go UNDER them: four of the six are
  // groups, each a landing page plus the pages that belong to it —
  // Check je laadpaal (the MID check · Laadpalen per merk, a page per
  // manufacturer of the register's models · "Nog geen laadpaal?", the
  // funnel for a visitor the check turns away), Aanbieders (overview · Voor
  // aanbieders · the provider portal), Over ons (About · Hoe wij vergelijken,
  // the section's methodology child; the breadcrumb trail already says About
  // → Hoe wij vergelijken) and Kennisbank (articles · Begrippenlijst ·
  // ERE-prijs). The lg density (tighter padding, tighter type, no gaps;
  // roomier again at xl) stays: the six, four of them with a chevron, still
  // have to clear 1024px beside the logo and the controls with a classic 15px
  // scrollbar. A child added to a group never widens the row — only its panel.
  const navEntries: NavEntry[] = [
    { name: t('nav.compare'), path: getComparerHref(language), primary: true },
    {
      id: 'charger',
      name: t('nav.mid_check'),
      path: `/${language}/mid-check`,
      children: [
        { name: t('nav.mid_check'), path: `/${language}/mid-check` },
        { name: t('nav.charger_brands'), path: `/${language}/laadpalen` },
        { name: t('nav.no_charger_v2'), path: `/${language}/geen-laadpaal` },
      ],
    },
    {
      id: 'providers',
      name: t('nav.providers'),
      path: providerHubPath(language),
      children: [
        { name: t('nav.providers'), path: providerHubPath(language) },
        // nav.for_providers, not nav.partner: that key said "Voor providers" and
        // is frozen that way in prd's CMS copy, with no admin field to change it.
        { name: t('nav.for_providers'), path: `/${language}/partner` },
        { name: t('nav.partner_portal'), path: `/${language}/partner-login` },
      ],
    },
    {
      id: 'about',
      name: t('nav.about'),
      path: `/${language}/about`,
      children: [
        { name: t('nav.about'), path: `/${language}/about` },
        { name: t('nav.how_we_compare'), path: `/${language}/hoe-wij-vergelijken` },
      ],
    },
    {
      id: 'kennisbank',
      name: t('nav.faq'),
      path: `/${language}/kennisbank`,
      children: [
        { name: t('nav.faq'), path: `/${language}/kennisbank` },
        { name: t('nav.glossary'), path: `/${language}/begrippen` },
        { name: t('nav.ere_price'), path: `/${language}/ere-prijs` },
      ],
    },
    { name: t('nav.contact'), path: `/${language}/contact` },
  ];
  // The drawer has no hover, so a group flattens: the parent at full weight,
  // then the children that are not the parent itself, indented under it.
  // Every destination lives in a group or on the row, so the drawer carries
  // nothing of its own ("(Nog) geen laadpaal?" was its last, drawer-only item;
  // the label reads "Nog geen laadpaal?" since 2026-10-21, nav.no_charger_v2).
  const drawerLinks: (NavLink & { nested?: boolean })[] = navEntries.flatMap((entry) =>
    isGroup(entry)
      ? [
          { name: entry.name, path: entry.path },
          ...entry.children
            .filter((child) => child.path !== entry.path)
            .map((child) => ({ ...child, nested: true })),
        ]
      : [entry]
  );

  const trackNav = (link: NavLink, variant: 'desktop' | 'mobile') => {
    trackEvent('nav_click', {
      nav_item: link.name,
      path: link.path,
      surface: SURFACES.header,
      variant,
      language,
    });
  };

  useEffect(() => {
    if (!isMenuOpen) return;
    return lockPageScroll();
  }, [isMenuOpen]);

  // Scroll-down state: below `lg` (hamburger header) the header slides fully
  // away while scrolling down (reading) and comes back on an upward scroll or
  // near the top of the page. From `lg` up (desktop nav row) it does not leave:
  // it compacts to half its height — 80px → 40px — so the nav stays one click
  // away while the reading column gets the room. Both are driven by this one
  // flag; the breakpoint split is pure CSS (see className below and the
  // `group-data-[compact]` utilities on the children). Never collapses while
  // the mobile drawer is open. SSR-safe: starts full-size, listener only
  // attaches client-side.
  //
  // WHEN it flips is the tracker's rule (src/lib/scrollChrome.ts): distance
  // travelled since the last change of direction, 48 px down to hide, 16 px
  // up to reveal. Until 2026-10-01 any 4 px delta flipped it, and on a phone a
  // scroll is a stream of small deltas both ways — so the header, and the
  // sticky filter bar riding --header-offset under it, jumped on every
  // settling finger and every URL-bar resize while the visitor was reading.
  const [headerHidden, setHeaderHidden] = useState(false);
  const tracker = useRef(createScrollChromeTracker());

  // Keyboard focus inside the header pins it open, so tabbing into the nav can
  // never land on something translated off-screen. This is React state rather
  // than a `focus-within:` class because --header-offset below has to agree with
  // it: as a CSS-only escape hatch it silently overrode the transform while the
  // variable still said "hidden", which pinned the header open after every nav
  // click (the clicked link keeps focus) and left the scroll-progress bar
  // stranded at top:0 underneath it — visible only on the way back up.
  // `:focus-visible`, not plain focus: a mouse click on a link must not pin.
  const [focusPinned, setFocusPinned] = useState(false);

  // Single source of truth for "is the header out of the way right now".
  const hidden = headerHidden && !focusPinned && !isMenuOpen;
  useEffect(() => {
    // Start from where the page IS, not from 0: after a restored or anchored
    // position the first event otherwise carried a delta of the whole
    // distance and hid the header before the visitor had moved a finger.
    const chrome = tracker.current;
    chrome.reset(window.scrollY);
    // When focus last moved (Tab, Shift+Tab, a script's focus()). The browser
    // then scrolls the focused control into view by itself, inside
    // `scroll-padding-top` — the header's CURRENT height (index.css). That
    // scroll is not the visitor reading either: a Shift+Tab that scrolled a
    // control up out from under a hidden or compact header used to count as
    // upward travel, and the header slid back over the control it had just
    // uncovered.
    let focusMovedAt = -Infinity;
    const onFocusIn = () => {
      focusMovedAt = performance.now();
    };
    // Plain passive listener, no rAF throttle on purpose: the tracker is a
    // handful of comparisons and only reports real transitions, so React is
    // asked to render exactly when the state flips and never in between.
    const onScroll = () => {
      const y = window.scrollY;
      // The drawer pins the header; a scroll this site started itself — a CTA
      // bringing the comparer or another anchor into view — or one the
      // browser made for a focus move is not the visitor reading. None counts
      // as travel: re-anchor, so the first scroll the visitor makes
      // afterwards is measured from where the page actually is, and the
      // target lands under the header instead of under the empty band it
      // would otherwise leave behind.
      if (
        isMenuOpen ||
        isProgrammaticScroll() ||
        performance.now() - focusMovedAt < FOCUS_SCROLL_WINDOW_MS
      ) {
        chrome.reset(y);
        return;
      }
      const next = chrome.update(y, document.documentElement.scrollHeight - window.innerHeight);
      if (next !== null) setHeaderHidden(next);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [isMenuOpen]);

  // A new page starts at its top, where the header is always in view. The
  // router's scroll manager says so itself (lib/pageScroll.ts
  // `scrollToNewPageTop`) instead of leaving it to a scroll event: with a hash
  // in the address it moves straight on to the anchor, a programmatic scroll
  // the listener above ignores, and it measures that anchor against
  // --header-offset right away. So the state is published here synchronously,
  // before the measurement; the render that follows only catches the classes
  // up (the [hidden] effect below then finds the attribute already gone).
  useEffect(
    () =>
      onNewPageTop(() => {
        tracker.current.show(window.scrollY);
        setHeaderHidden(false);
        setHeaderState(document.documentElement, false);
      }),
    []
  );

  // Publish the scroll-down state on <html> so everything pinned "just below
  // the header" (reading-progress bar, sticky filter/column rows) can follow
  // its live bottom edge through `--header-offset` (index.css resolves the
  // attribute to 0px below lg — header gone — and 2.5rem from lg — header
  // compact — so the value is breakpoint-correct without any JS media query,
  // and stays correct across a resize). A DOM attribute rather than context on
  // purpose: a context update would re-render the whole comparison tree
  // underneath. setHeaderState also glides the sticky rows that follow the
  // header (`data-follows-header`, src/lib/headerFollow.ts) from where they
  // were to where the new offset puts them — so it must see the state it is
  // leaving, which is why the attribute is cleared on unmount only, not in a
  // cleanup that would run between every two states.
  useEffect(() => {
    setHeaderState(document.documentElement, hidden);
  }, [hidden]);
  useEffect(
    () => () => {
      document.documentElement.removeAttribute(HEADER_STATE_ATTR);
    },
    []
  );

  // The row's one menu (MenuState). menuRef is the source of truth and every
  // change goes through commitMenu, which also triggers the render: a pointer
  // event that lands between a timer's change and React's commit still reads
  // the menu as it now is. Two timers, both owned by one group:
  //  - the open intent: entering a group starts it, leaving that group drops
  //    it; when it fires it cancels any close grace and opens its group (a
  //    swap when another panel is open or still fading);
  //  - the close grace: armed only by the pointer leaving the OPEN, unpinned
  //    group, and it closes only that group. Entering the open group again
  //    cancels it; entering another group leaves it running.
  // Leaving A and entering B are one pointer event, so B's intent (+120ms) is
  // due before A's grace (+150ms) and cancels it: a deliberate switch never
  // passes through "nothing open" and never shows two panels, while a pointer
  // that only crosses B lets A close 150ms after it left A.
  const [menu, setMenu] = useState<MenuState | null>(null);
  const menuRef = useRef<MenuState | null>(null);
  const openIntent = useRef<{ id: string; timer: number } | null>(null);
  const closeGrace = useRef<{ id: string; timer: number } | null>(null);
  // Until when a panel that just closed is still fading out. An open inside
  // that window counts as a swap as well, so a new panel never fades in over
  // the tail of the old one.
  const fadeUntil = useRef(0);

  const commitMenu = useCallback((next: MenuState | null) => {
    const prev = menuRef.current;
    if (prev === next) return;
    if (prev && !next) fadeUntil.current = performance.now() + PANEL_FADE_MS;
    menuRef.current = next;
    setMenu(next);
  }, []);
  const cancelOpenIntent = useCallback(() => {
    if (openIntent.current) {
      window.clearTimeout(openIntent.current.timer);
      openIntent.current = null;
    }
  }, []);
  const cancelCloseGrace = useCallback(() => {
    if (closeGrace.current) {
      window.clearTimeout(closeGrace.current.timer);
      closeGrace.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      cancelOpenIntent();
      cancelCloseGrace();
    },
    [cancelOpenIntent, cancelCloseGrace]
  );

  /** Open `id` — keeps an already open menu as it is, except that a pin sticks. */
  const openMenu = useCallback(
    (id: string, pinned: boolean) => {
      const prev = menuRef.current;
      if (prev?.id === id) {
        if (pinned && !prev.pinned) commitMenu({ ...prev, pinned: true });
        return;
      }
      commitMenu({ id, pinned, swapped: prev !== null || performance.now() < fadeUntil.current });
    },
    [commitMenu]
  );
  /** Close the menu; with `id`, only when that group is the open one. */
  const closeMenu = useCallback(
    (id?: string) => {
      cancelOpenIntent();
      cancelCloseGrace();
      const prev = menuRef.current;
      if (prev && (id === undefined || prev.id === id)) commitMenu(null);
    },
    [cancelOpenIntent, cancelCloseGrace, commitMenu]
  );
  const hoverStart = (id: string) => {
    cancelOpenIntent();
    if (menuRef.current?.id === id) {
      // Back on the open group: it stays, pinned or not.
      cancelCloseGrace();
      return;
    }
    openIntent.current = {
      id,
      timer: window.setTimeout(() => {
        openIntent.current = null;
        cancelCloseGrace();
        openMenu(id, false);
      }, HOVER_OPEN_MS),
    };
  };
  const hoverEnd = (id: string) => {
    if (openIntent.current?.id === id) cancelOpenIntent();
    const open = menuRef.current;
    // Leaving a closed group, or a pinned menu, arms nothing.
    if (open?.id !== id || open.pinned) return;
    cancelCloseGrace();
    closeGrace.current = {
      id,
      timer: window.setTimeout(() => {
        closeGrace.current = null;
        const now = menuRef.current;
        if (now?.id === id && !now.pinned) commitMenu(null);
      }, HOVER_CLOSE_MS),
    };
  };
  // The chevron: a pinned menu closes; anything else — closed, or opened by
  // hover — opens pinned. So a click on a menu the hover just opened keeps it
  // (until 2026-10-03 that click closed the panel the visitor had come for).
  const toggleMenu = (id: string) => {
    cancelOpenIntent();
    cancelCloseGrace();
    const prev = menuRef.current;
    if (prev?.id === id) {
      commitMenu(prev.pinned ? null : { ...prev, pinned: true });
      return;
    }
    commitMenu({
      id,
      pinned: true,
      swapped: prev !== null || performance.now() < fadeUntil.current,
    });
  };
  const pinOpen = (id: string) => {
    cancelOpenIntent();
    cancelCloseGrace();
    openMenu(id, true);
  };

  // A new page, or the header compacting out of the way, closes the menu: a
  // floating panel must not outlive the bar it hangs from.
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);
  useEffect(() => {
    if (hidden) closeMenu();
  }, [hidden, closeMenu]);

  const isActive = (path: string) => {
    // "Vergelijken" carries the comparison's start, `#start` (getComparerHref):
    // it is the homepage's link, and the homepage reached through it is too.
    const startSuffix = `#${COMPARER_START_ID}`;
    const target = path.endsWith(startSuffix) ? path.slice(0, -startSuffix.length) : path;
    if (target === `/${language}/` || target === `/${language}`) {
      return (
        (location.pathname === `/${language}/` || location.pathname === `/${language}`) &&
        (!location.hash || location.hash === startSuffix)
      );
    }
    if (path.includes('#')) {
      const [p, h] = path.split('#');
      return location.pathname === p && location.hash === `#${h}`;
    }
    return location.pathname === path;
  };

  return (
    <header
      onFocus={(e) => {
        // Only keyboard focus pins. A pointer click on a nav link/toggle leaves
        // focus on it without matching :focus-visible, so it can't jam the
        // header open for the rest of the page.
        if (e.target instanceof Element && e.target.matches(':focus-visible')) {
          setFocusPinned(true);
        }
      }}
      onBlur={(e) => {
        // relatedTarget check: moving between two links inside the header must
        // not unpin for a frame (that flickers the header and the offset).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setFocusPinned(false);
        }
      }}
      // `data-compact` + `group`: the children shrink themselves with
      // `lg:group-data-[compact]:*` utilities, so the compact look is declared
      // next to each element's normal look instead of in a second class list.
      data-compact={hidden ? '' : undefined}
      className={cn(
        // `transition` (not transition-colors) so the hide/show transform
        // animates alongside the theme color transitions.
        // The frosted glass is desktop-only (lg): below that the bar slides
        // in and out of view on every change of scroll direction, and a
        // backdrop-filter re-samples the page under it on every frame of that
        // transform — the single most expensive thing on a phone GPU here.
        // At 95 % opacity the solid fallback reads the same.
        'group sticky top-0 z-50 w-full bg-slate-900 lg:bg-slate-900/95 lg:backdrop-blur-xl border-b border-slate-800/60 shadow-sm transition duration-300 motion-reduce:transition-none',
        // Collapsed: below lg slide the whole bar away; from lg shift it up by
        // half its height. Transforms only, so the document never reflows and
        // the content under the header never jumps — the row below re-centres
        // itself in the visible bottom half with a matching counter-shift.
        hidden && 'max-lg:-translate-y-full lg:-translate-y-10'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 transition-transform duration-300 motion-reduce:transition-none lg:group-data-[compact]:translate-y-5">
          <Link
            to={`/${language}`}
            className="flex items-center gap-2 shrink-0"
            onClick={() => {
              trackEvent('logo_click', { surface: SURFACES.header, language });
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <LogoMark
              className="w-10 h-10 text-emerald-500 transition-all duration-300 motion-reduce:transition-none lg:group-data-[compact]:w-7 lg:group-data-[compact]:h-7"
              title="ERE-Vergelijk"
            />
            {/* Wordmark scales down / hides on narrow screens: with the shrink-0
                controls (theme + language + hamburger, ~173px) beside it, the full
                text-2xl wordmark only fits from ~420px viewport width. */}
            <span className="hidden min-[360px]:inline text-sm min-[420px]:text-2xl font-black tracking-tight text-white transition-[font-size] duration-300 motion-reduce:transition-none lg:group-data-[compact]:text-lg">
              ERE-
              <span className="text-emerald-400 [html.light_&]:text-emerald-700">Vergelijk</span>
            </span>
          </Link>

          {/* Desktop Nav — shown from lg up. The row doesn't fit beside the
              logo + controls at portrait-tablet widths (md, 768–1023px), where
              it used to overflow and push the language toggle off-screen (and
              let the whole page drag sideways on touch). Below lg we use the
              hamburger drawer instead.
              At lg the items are packed shoulder-to-shoulder (no gap, px-2,
              13px) so they clear 1024px; xl gets the roomier spacing back.
              Never `hidden xl:*` on an item — there is no hamburger from lg
              up, so a hidden item is an unreachable item.
              Alignment: the row stays balanced BETWEEN the logo and the
              controls (justify-between), not centred on the page — with a
              wordmark ~60px wider than the controls, page-centring would leave
              ~14px between the wordmark and the pill. Every item ends where
              its text (or chevron ink) ends plus the same inset its text
              starts at, so the rhythm from one label to the next is 16px at lg
              and 36px at xl whether an item has a chevron or not; and the
              pill's margin equals that inset, so the wordmark → pill gap
              equals the "Contact" → theme-button gap. */}
          <nav className="hidden lg:flex items-center space-x-0 xl:space-x-1">
            {navEntries.map((entry) =>
              isGroup(entry) ? (
                <NavGroupDisclosure
                  key={entry.id}
                  group={entry}
                  toggleLabel={t('nav.submenu_toggle').replace('{{item}}', entry.name)}
                  active={
                    isActive(entry.path) || entry.children.some((child) => isActive(child.path))
                  }
                  isActive={isActive}
                  open={menu?.id === entry.id}
                  swapped={menu?.id === entry.id && menu.swapped}
                  othersOpen={menu !== null && menu.id !== entry.id}
                  onHoverStart={() => hoverStart(entry.id)}
                  onHoverEnd={() => hoverEnd(entry.id)}
                  onToggle={() => toggleMenu(entry.id)}
                  onOpen={() => pinOpen(entry.id)}
                  onClose={() => closeMenu(entry.id)}
                  onNavigate={(link) => {
                    trackNav(link, 'desktop');
                    closeMenu();
                  }}
                />
              ) : (
                <Link
                  key={entry.path}
                  to={entry.path}
                  // No scroll of its own for "Vergelijken": its `#start` makes
                  // the router's scroll manager move the page, on the homepage
                  // too (ScrollToTop.tsx).
                  onClick={() => trackNav(entry, 'desktop')}
                  className={cn(
                    NAV_ITEM_BASE,
                    entry.primary
                      ? 'bg-emerald-700 text-white hover:bg-emerald-800 shadow-md shadow-emerald-900/20 ml-2 xl:ml-4'
                      : isActive(entry.path)
                        ? NAV_ITEM_ACTIVE
                        : NAV_ITEM_IDLE
                  )}
                >
                  {entry.name}
                </Link>
              )
            )}
          </nav>

          {/* Same lg-tightening as the nav row: the row has to clear 1024px
              next to the wordmark, and 16px of gap here is cheaper than a
              smaller wordmark or a hidden link. Roomy again from xl. The
              language buttons take lg:px-1.5: with four chevrons in the row,
              that 8px is what keeps 16px+ of slack at 1024px in English with
              a classic 15px scrollbar. The compact bar keeps px-1.5 at lg and
              its own px-2 from xl, so the buttons never grow as it compacts. */}
          <div className="flex items-center gap-2 md:gap-4 lg:gap-2 xl:gap-4 shrink-0">
            {/* Theme Toggle */}
            <button
              onClick={() => {
                trackEvent('theme_toggle', {
                  from: theme,
                  to: theme === 'dark' ? 'light' : 'dark',
                  surface: SURFACES.header,
                  language,
                });
                toggleTheme();
              }}
              className="flex items-center justify-center w-9 h-9 lg:group-data-[compact]:w-7 lg:group-data-[compact]:h-7 rounded-xl bg-slate-800 text-slate-400 hover:text-emerald-500 hover:bg-slate-700 transition-all focus-ring"
              aria-label={theme === 'dark' ? t('nav.theme_light') : t('nav.theme_dark')}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Language Toggle */}
            <div className="flex items-center bg-slate-800 rounded-xl p-1 lg:group-data-[compact]:p-0.5 shrink-0 transition-all">
              <button
                onClick={() => {
                  if (language !== 'nl') {
                    trackEvent('language_toggle', {
                      from: language,
                      to: 'nl',
                      surface: SURFACES.header,
                      language,
                    });
                  }
                  setLanguage('nl');
                }}
                className={`px-2.5 py-1 md:px-3 md:py-1.5 lg:px-1.5 xl:px-3 lg:group-data-[compact]:px-1.5 xl:group-data-[compact]:px-2 lg:group-data-[compact]:py-0.5 text-xs font-black rounded-lg transition-all focus-ring ${
                  language === 'nl'
                    ? 'bg-slate-700 text-emerald-400 shadow-sm [html.light_&]:bg-[#ffffff] [html.light_&]:text-[#065f46]'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                NL
              </button>
              <button
                onClick={() => {
                  if (language !== 'en') {
                    trackEvent('language_toggle', {
                      from: language,
                      to: 'en',
                      surface: SURFACES.header,
                      language,
                    });
                  }
                  setLanguage('en');
                }}
                className={`px-2.5 py-1 md:px-3 md:py-1.5 lg:px-1.5 xl:px-3 lg:group-data-[compact]:px-1.5 xl:group-data-[compact]:px-2 lg:group-data-[compact]:py-0.5 text-xs font-black rounded-lg transition-all focus-ring ${
                  language === 'en'
                    ? 'bg-slate-700 text-emerald-400 shadow-sm [html.light_&]:bg-[#ffffff] [html.light_&]:text-[#065f46]'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                EN
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => {
                const next = !isMenuOpen;
                trackEvent('mobile_menu_toggle', {
                  open: next,
                  surface: SURFACES.header,
                  language,
                });
                setIsMenuOpen(next);
              }}
              aria-label={isMenuOpen ? t('nav.close_menu') : t('nav.open_menu')}
              aria-expanded={isMenuOpen}
              className="lg:hidden p-2 text-slate-400 hover:bg-slate-800 rounded-xl transition-all focus-ring"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu: right-side drawer with backdrop, portaled out of the sticky+backdrop-filter header */}
      <ClientPortal>
        <AnimatePresence>
          {isMenuOpen && (
            <>
              <m.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setIsMenuOpen(false)}
                className="lg:hidden fixed inset-0 z-[55] bg-slate-950/75 touch-none"
                aria-hidden="true"
              />
              <m.aside
                key="drawer"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', ease: [0.22, 1, 0.36, 1], duration: 0.32 }}
                className="lg:hidden fixed inset-y-0 right-0 z-[60] w-[70%] max-w-[18rem] bg-slate-900 border-l border-slate-800/70 shadow-2xl flex flex-col"
                role="dialog"
                aria-modal="true"
                aria-label={t('nav.menu')}
              >
                <div className="flex items-center justify-between px-5 h-20 border-b border-slate-800/60">
                  <span className="text-xs font-black uppercase tracking-[0.3em] text-fg-muted">
                    {t('nav.menu')}
                  </span>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 text-slate-400 hover:text-emerald-500 hover:bg-slate-800 rounded-xl transition-all focus-ring"
                    aria-label={t('nav.close_menu')}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
                  {drawerLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => {
                        trackNav(link, 'mobile');
                        setIsMenuOpen(false);
                      }}
                      className={cn(
                        'block rounded-2xl transition-all',
                        // A group child stays a plain row, indented under its
                        // parent behind a left rule, one step smaller and one
                        // step quieter — so "Hoe wij vergelijken" reads as part
                        // of "Over ons" without an accordion: four groups add
                        // only seven indented rows between them, so a fold on
                        // each would cost every visitor a tap to reach a page
                        // that is a short scroll away anyway, while the drawer
                        // has no other folds to teach the gesture.
                        link.nested
                          ? 'ml-5 rounded-l-none border-l-2 border-slate-800 px-4 py-3 text-sm font-bold'
                          : 'px-4 py-4 text-base font-black',
                        isActive(link.path)
                          ? 'bg-emerald-900/20 text-emerald-300'
                          : link.nested
                            ? 'text-slate-400 hover:bg-slate-800 hover:text-emerald-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-emerald-500'
                      )}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
              </m.aside>
            </>
          )}
        </AnimatePresence>
      </ClientPortal>
    </header>
  );
};
